import { HelpfulQuestion } from '@buoy-components/user-feedback';
import Grid from '@mui/material/Grid';
import { trackStructuredEvent } from 'src/analytics';
import { getCategory } from 'src/interviews/analytics';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import TriageHeading from 'src/interviews/components/TriageHeading';
import useInterview from 'src/interviews/hooks/useInterview';
import RichEndings from '../components/RichEndings';
import CollapsibleResults from '../../CollapsibleResults';
import { formatSentenceCase } from 'src/interviews/utils/formatTitleCasing';
import Pill from '../DiagnosisOfConcern/components/Pill';
import DifferentialCard from '../../DifferentialCard';
import { useMemo } from 'react';
import useAppContext from 'src/hooks/useAppContext';
import { buildDifferentialFromPotentialCondition } from './utils';
import MessageBox from '../../MessageBox';
import { getCompletionMessage } from './getCompletionMessage';

import type { IDiagnosis, IInterviewResults } from 'src/packages/polaris/types';

const EmergencyCompletion = ({ result }: { result: IInterviewResults }) => {
  const description = getCompletionMessage(result);
  const interview = useInterview();
  const { interviewSettings } = useAppContext();

  const symptomInterview = interviewSettings?.find(
    (interview) => interview.interview === 'symptoms',
  );

  const potentialConditionAsDifferential: IDiagnosis | null = useMemo(
    () => buildDifferentialFromPotentialCondition(result),
    [result],
  );

  return (
    <Grid item className="!bp-max-w-full">
      <CollapsibleResults isShowingResults>
        <Grid container direction="column" spacing={2.5}>
          <Grid item>
            <TriageHeading triage={result.triage} />
          </Grid>
          <Grid item>
            <Paragraph>{description}</Paragraph>
          </Grid>
          {Boolean(result?.triage_explanation?.triage_factors?.length) && (
            <Grid item>
              <Grid item className="bp-w-full">
                <Paragraph className="!bp-mb-3 bp-w-full bp-text-base">
                  Alarming factors include:
                </Paragraph>
                <div className="bp-flex bp-w-full bp-flex-wrap bp-gap-2 bp-pb-2">
                  {result.triage_explanation.triage_factors?.map((label) => (
                    <Pill key={label} label={formatSentenceCase(label)} variant="check" />
                  ))}
                </div>
              </Grid>
            </Grid>
          )}
          {potentialConditionAsDifferential && (
            <Grid item>
              <Paragraph className="!bp-mb-3 bp-w-full bp-text-base">
                Possible causes might include:
              </Paragraph>
              {potentialConditionAsDifferential && (
                <DifferentialCard differential={potentialConditionAsDifferential} />
              )}
            </Grid>
          )}
          {Boolean(result.differential?.length) && (
            <Grid item className="bp-w-full">
              <Paragraph className="!bp-mb-3 bp-w-full bp-text-base">
                Possible causes might include:
              </Paragraph>
              <Grid
                container
                flexDirection="row"
                flexWrap="nowrap"
                alignItems="stretch"
                className="bp-no-scrollbar bp-gap-3 bp-overflow-x-scroll"
              >
                {result.differential?.map((match) => (
                  <DifferentialCard
                    differential={match}
                    showTreatmentPlan
                    className="!bp-h-auto bp-max-w-[262px] bp-shrink-0"
                    key={match.token}
                  />
                ))}
              </Grid>
            </Grid>
          )}
          {Boolean(result.alarm) && (
            <Grid item>
              <MessageBox
                header="Alarming scenario warning"
                alarmMessage="If this doesn’t seem right, you may want to consider reviewing your symptoms with a medical provider or continuing with self-assessment below."
                color="red"
              />
            </Grid>
          )}
          <Grid item>
            <HelpfulQuestion
              snowplowProps={{
                action: getCategory('emergency'),
                label: interview.token || '',
              }}
              trackingCallback={trackStructuredEvent}
              withBuoyTheme
            />
          </Grid>
        </Grid>
        <RichEndings
          interviewType="emergency"
          result={result}
          isAlarmContinuation={result.alarm && !!result._links?.continuation}
          symptomInterview={symptomInterview}
        />
      </CollapsibleResults>
    </Grid>
  );
};

export default EmergencyCompletion;
